import { useState } from "react";
import { commentFeed, type CommentItem } from "@/mocks/comments";

type Sentiment = "all" | CommentItem["sentiment"];

const filters: { key: Sentiment; label: string; icon: string }[] = [
  { key: "all", label: "全部热评", icon: "ri-chat-3-line" },
  { key: "positive", label: "好评", icon: "ri-thumb-up-line" },
  { key: "neutral", label: "中立围观", icon: "ri-eye-line" },
  { key: "negative", label: "吐槽差评", icon: "ri-thumb-down-line" },
];

const sentimentBadge: Record<CommentItem["sentiment"], string> = {
  positive: "bg-primary-100 text-primary-700",
  neutral: "bg-accent-100 text-accent-900",
  negative: "bg-secondary-100 text-secondary-900",
};

const sentimentLabel: Record<CommentItem["sentiment"], string> = {
  positive: "好评",
  neutral: "中立",
  negative: "差评",
};

function Avatar({ user }: { user: string }) {
  const palette = ["bg-primary-200 text-primary-800", "bg-accent-200 text-accent-900", "bg-secondary-200 text-secondary-900"];
  const idx = user.length % palette.length;
  return (
    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${palette[idx]}`}>
      <i className="ri-user-3-line text-lg" aria-hidden="true" />
    </span>
  );
}

export default function CommentBoard() {
  const [active, setActive] = useState<Sentiment>("all");
  const list = active === "all" ? commentFeed : commentFeed.filter((c) => c.sentiment === active);

  const countOf = (s: Sentiment) =>
    s === "all" ? commentFeed.length : commentFeed.filter((c) => c.sentiment === s).length;

  return (
    <section id="comments" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="font-bebas text-sm tracking-[0.2em] text-primary-500">COMMENT SQUAD</p>
            <h2 className="font-heading mt-2 text-2xl font-bold text-foreground-950 md:text-3xl">
              评论区热评 · <span className="text-primary-500">围观画风一览</span>
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600">
              从各平台高赞评论中挑出的代表观点，还原哇偶姐姐评论区到底是群嘲为主，还是理性与支持并存。
            </p>
          </div>
          <div className="inline-flex flex-wrap items-center gap-1 rounded-full bg-background-200 p-1">
            {filters.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setActive(f.key)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  active === f.key
                    ? "bg-background-50 font-semibold text-foreground-950"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                <i className={f.icon} aria-hidden="true" />
                {f.label}
                <span className="text-xs text-foreground-400">{countOf(f.key)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {list.map((c) => (
            <article
              key={c.id}
              className="flex flex-col rounded-2xl border border-background-200 bg-background-100 p-5 transition-colors hover:border-accent-200"
            >
              <div className="flex items-center gap-3">
                <Avatar user={c.user} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground-900">{c.user}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">
                    {c.time} · <i className={`${c.platformIcon} mr-0.5`} aria-hidden="true" />{c.platform}
                  </p>
                </div>
                <span className={`inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium ${sentimentBadge[c.sentiment]}`}>
                  {sentimentLabel[c.sentiment]}
                </span>
              </div>
              <p className="mt-4 flex-1 text-sm leading-6 text-foreground-800">{c.text}</p>
              <div className="mt-4 flex items-center gap-4 border-t border-background-200 pt-3 text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1"><i className="ri-heart-3-fill text-primary-400" aria-hidden="true" />{c.likes}</span>
                <span className="inline-flex items-center gap-1"><i className="ri-chat-1-line" aria-hidden="true" />{c.replies} 回复</span>
                <span className="ml-auto inline-flex items-center gap-1"><i className="ri-share-line" aria-hidden="true" />分享</span>
              </div>
            </article>
          ))}
        </div>

        <p className="mt-6 text-center text-xs text-foreground-500">
          以上评论为简化演示样本，用于还原讨论画风，不代表任何真实用户的观点与身份。
        </p>
      </div>
    </section>
  );
}